import { PencilSimple, Check, X } from "@phosphor-icons/react";

/**
 * Small click-to-edit inline field for platform-level ad copy (description,
 * cta, hashtags). Renders the value read-only with a pencil-on-hover; when
 * clicked, swaps to an input + save/cancel buttons.
 *
 * `hashtags` are stored as a comma-separated draft string in the parent and
 * split back to a list on save.
 */
export default function EditableField({
  label,
  fieldKey,
  value,
  isEditing,
  draft,
  setDraft,
  saving,
  onStart,
  onCancel,
  onSave,
  multiline = false,
  maxLength,
  hint,
  placeholder = "",
  platform,
}) {
  if (isEditing) {
    const Input = multiline ? "textarea" : "input";
    return (
      <div className="border border-[#ccff00]/40 bg-[#ccff00]/5 p-2">
        <div className="label-mono mb-1 flex items-center justify-between">
          <span>{label} · editing</span>
          {maxLength && (
            <span className="text-[10px] text-zinc-500">
              {draft.length}/{maxLength}
            </span>
          )}
        </div>
        <Input
          data-testid={`edit-${fieldKey}-${platform}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={maxLength}
          placeholder={placeholder}
          rows={multiline ? 3 : undefined}
          className="w-full bg-[#0a0a0a] border border-white/10 text-sm text-zinc-100 px-2 py-1.5 focus:border-[#ccff00] focus:outline-none resize-none"
          autoFocus
        />
        {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
        <div className="flex gap-1 mt-2">
          <button
            data-testid={`save-${fieldKey}-${platform}`}
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-1 px-2 py-1 bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-mono text-[10px] font-bold uppercase tracking-wider"
          >
            <Check size={11} weight="bold" />
            {saving ? "saving…" : "save"}
          </button>
          <button
            data-testid={`cancel-${fieldKey}-${platform}`}
            onClick={onCancel}
            disabled={saving}
            className="flex items-center gap-1 px-2 py-1 bg-[#121212] border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 disabled:opacity-40 font-mono text-[10px] uppercase tracking-wider"
          >
            <X size={11} weight="bold" />
            cancel
          </button>
        </div>
      </div>
    );
  }

  const hasValue = value && String(value).trim().length > 0;
  return (
    <div
      className="group cursor-pointer"
      onClick={onStart}
      data-testid={`edit-trigger-${fieldKey}-${platform}`}
    >
      <div className="label-mono mb-1 flex items-center justify-between">
        <span>{label}</span>
        <PencilSimple
          size={11}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500"
        />
      </div>
      {hasValue ? (
        <div className="text-xs text-zinc-400 group-hover:text-zinc-200 transition-colors">
          {value}
        </div>
      ) : (
        <div className="text-xs text-zinc-600 italic group-hover:text-zinc-400 transition-colors">
          {placeholder || `Click to add ${label}`}
        </div>
      )}
    </div>
  );
}
